import { type ReactNode } from 'react';

import {
  COLOR_TOKENS,
  type ColorToken,
  CountPill,
  DonutChart,
  type DonutSegment,
  LegendRow,
  LineChart,
  type LineChartSeries,
  PageHeader,
  VerticalBarChart,
  type VerticalBarChartBar,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import {
  type ActivitySeries,
  type EngagementResponse,
  type LoginRecency,
  type ProfileSignals,
} from '@backrs_ui/grow-ui/src/redux/coachingAnalytics';

import { Box, type SxProps, type Theme, Tooltip, Typography } from '@mui/material';
import { type SystemStyleObject } from '@mui/system';

import {
  bookmarkTypeLabel,
  humanize,
  percentOf,
  percentShares,
  profileSignalLabel,
  recencyLabel,
} from './analytics.labels';
import { ENGAGEMENT_TOP } from './analytics.layouts';
import { mutedTextSx, pieLegendSx, pieRowSx, selectableRowSx } from './analytics.styles';
import { AnalyticsCard } from './AnalyticsCard';
import { AnalyticsCardGrid } from './AnalyticsCardGrid';
import {
  useAgentContext,
  useDetailAgentContext,
  useTopCardsAgentContext,
} from './EngagementCards.agentContext';
import { type SegmentFilter } from './StudentDataModal';
import { useStudentData } from './useStudentData';
import { COLUMNS, COMMON_COLUMNS, activityColumns } from './viewData.columns';

const { engagement, assessmentCoverage, hover } = appStrings.modules.coachingAnalytics;

const RECENCY_COLUMNS = [...COMMON_COLUMNS, COLUMNS.lastLogin, COLUMNS.loginRecency];
const NETWORK_COLUMNS = [...COMMON_COLUMNS, COLUMNS.networks];
const PROFILE_COLUMNS = [...COMMON_COLUMNS, COLUMNS.profileFacts];
const ADOPTION_COLUMNS = [
  ...COMMON_COLUMNS,
  COLUMNS.blueprintCompleted,
  COLUMNS.ayotCompleted,
  COLUMNS.lifeReadinessStatus,
];
const BOOKMARK_COLUMNS = [...COMMON_COLUMNS, COLUMNS.bookmarkTypes, COLUMNS.bookmarks];

/** Bookmark types left off the Bookmarks card. */
const HIDDEN_BOOKMARK_TYPES: ReadonlySet<string> = new Set([
  'tool',
  'nugget',
  'profile',
  'generated_content',
  'career_track',
  'educational_investment',
]);

const BAR_FILL: ColorToken = 'Brand/600';

type RecencyBucketId = keyof LoginRecency['buckets'];

const RECENCY_BUCKETS: readonly { id: RecencyBucketId; label: string; color: ColorToken }[] = [
  { id: 'last_30', label: recencyLabel('last_30'), color: 'Blue/800' },
  { id: 'days_31_60', label: recencyLabel('days_31_60'), color: 'Blue/500' },
  { id: 'days_61_90', label: recencyLabel('days_61_90'), color: 'Blue/300' },
  { id: 'over_90', label: recencyLabel('over_90'), color: 'Blue/100' },
];

type ActivityKey = 'posts' | 'goals' | 'comments';
export type ActivityLine = Readonly<{ key: ActivityKey; label: string; color: ColorToken }>;

const ACTIVITY_SERIES: readonly ActivityLine[] = [
  { key: 'posts', label: engagement.activity.studentPosts, color: 'Brand/800' },
  { key: 'goals', label: engagement.activity.studentGoals, color: 'Brand/500' },
  { key: 'comments', label: engagement.activity.studentComments, color: 'Brand/300' },
];

const NETWORK_ROW_LIMIT = 8;
const NETWORK_TYPE_COLORS: readonly ColorToken[] = [
  'Brand/900',
  'Brand/700',
  'Brand/500',
  'Brand/300',
  'Brand/100',
];
const PIE_SIZE = 150;

const monthLabel = (value: string): string => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
};

type TopCardProps = Readonly<{ data: EngagementResponse; onClick?: () => void }>;

/** Login-recency donut with its legend; `onSelect` makes segments and legend rows clickable. */
export const RecencyDonut = ({
  id,
  recency,
  idPrefix,
  onSelect,
}: Readonly<{
  id?: string;
  recency: LoginRecency;
  idPrefix: string;
  onSelect?: (bucket: RecencyBucketId, label: string) => void;
}>) => {
  const { buckets, total } = recency;
  const shares = percentShares(
    RECENCY_BUCKETS.map((bucket) => buckets[bucket.id].count),
    total,
  );
  const rows = RECENCY_BUCKETS.map((bucket, index) => ({
    ...bucket,
    count: buckets[bucket.id].count,
    pct: shares[index],
  }));
  const segments: DonutSegment[] = rows.map((row) => ({
    label: row.label,
    value: row.pct,
    color: row.color,
  }));
  const select = onSelect && ((index: number) => onSelect(rows[index].id, rows[index].label));

  return (
    <Box sx={pieRowSx}>
      <DonutChart
        id={id}
        variant={{ type: 'multi', segments }}
        size={PIE_SIZE}
        cutout={0}
        onSegmentClick={select}
      />
      <Box sx={pieLegendSx}>
        {rows.map((row, index) => (
          <LegendRow
            key={row.id}
            id={`${idPrefix}-${row.id}`}
            color={row.color}
            label={row.label}
            pct={row.pct}
            count={row.count}
            onClick={select && (() => select(index))}
          />
        ))}
      </Box>
    </Box>
  );
};

const LastActiveCard = ({ data, onClick }: TopCardProps) => {
  const students = useStudentData(
    'coaching-analytics-card-recently-active',
    engagement.recency.title,
    RECENCY_COLUMNS,
  );

  return (
    <AnalyticsCard
      title={engagement.recency.title}
      subtitle={engagement.recency.subtitle}
      info={[engagement.recency.info]}
      id="coaching-analytics-card-recently-active"
      onClick={onClick}
      viewData={students}
    >
      <RecencyDonut
        id="coaching-analytics-recency-donut"
        recency={data.recently_active}
        idPrefix="coaching-analytics-recency-legend"
        onSelect={(bucket, label) =>
          students.openSegment({
            label,
            where: [
              bucket === 'over_90'
                ? { field: 'login_recency', op: 'in', value: ['over_90', 'never'] }
                : { field: 'login_recency', op: 'eq', value: bucket },
            ],
          })
        }
      />
    </AnalyticsCard>
  );
};

export const ActivityLegend = ({ lines }: Readonly<{ lines: readonly ActivityLine[] }>) => (
  <Box sx={activityLegendSx}>
    {lines.map((line) => (
      <LegendRow key={line.key} color={line.color} label={line.label} />
    ))}
  </Box>
);

/** Monthly activity lines with a month axis and one hover region per month. */
export const ActivityLineChart = ({
  months,
  lines,
  ariaLabel,
  hoverText,
}: Readonly<{
  months: ActivitySeries['series'];
  lines: readonly ActivityLine[];
  ariaLabel: string;
  hoverText: (month: string, entry: ActivitySeries['series'][number]) => string;
}>) => {
  const series: readonly LineChartSeries[] = lines.map((line) => ({
    id: line.key,
    points: months.map((month) => month[line.key]),
    color: line.color,
  }));

  return (
    <>
      <Box sx={activityChartWrapSx}>
        <LineChart
          series={series}
          ariaLabel={ariaLabel}
          showValues
          pointRadius={ACTIVITY_POINT_RADIUS}
          minScale={ACTIVITY_SCALE_FLOOR}
        />
        <Box sx={activityHoverRowSx}>
          {months.map((month, index) => (
            <Tooltip
              key={month.index}
              arrow
              placement="top"
              followCursor
              title={hoverText(monthLabel(month.start), month)}
            >
              <Box sx={activityHoverCellSx(index === 0 || index === months.length - 1)} />
            </Tooltip>
          ))}
        </Box>
      </Box>
      <Box sx={weekAxisSx}>
        {months.map((month) => (
          <Typography key={month.index} variant="text-xs-medium" sx={weekLabelSx}>
            {monthLabel(month.start)}
          </Typography>
        ))}
      </Box>
    </>
  );
};

const ActivityCard = ({ data, onClick }: TopCardProps) => {
  const months = data.activity.monthly.series;

  const students = useStudentData('coaching-analytics-card-activity', engagement.activity.title, [
    ...COMMON_COLUMNS,
    ...activityColumns(months.length),
  ]);

  return (
    <AnalyticsCard
      title={engagement.activity.title}
      subtitle={engagement.activity.subtitle(months.length)}
      info={[engagement.activity.info]}
      id="coaching-analytics-card-activity"
      onClick={onClick}
      viewData={students}
      belowHeader={<ActivityLegend lines={ACTIVITY_SERIES} />}
    >
      <ActivityLineChart
        months={months}
        lines={ACTIVITY_SERIES}
        ariaLabel={engagement.activity.title}
        hoverText={(month, entry) =>
          engagement.activity.month(month, entry.posts, entry.goals, entry.comments)
        }
      />
    </AnalyticsCard>
  );
};

const NetworkMembershipCard = ({ data }: Readonly<{ data: EngagementResponse }>) => {
  const rows = data.networks.slice(0, NETWORK_ROW_LIMIT);
  const max = Math.max(...rows.map((row) => row.count), 0);

  const typeLabel = (type: string | null): string =>
    type ? humanize(type) : engagement.networkMembership.otherType;

  const types = rows
    .map((row) => typeLabel(row.type))
    .filter((type, index, all) => all.indexOf(type) === index);
  const colorOf = (type: string | null): ColorToken =>
    NETWORK_TYPE_COLORS[
      Math.min(Math.max(types.indexOf(typeLabel(type)), 0), NETWORK_TYPE_COLORS.length - 1)
    ];

  const students = useStudentData(
    'coaching-analytics-card-network-membership',
    engagement.networkMembership.title,
    NETWORK_COLUMNS,
  );

  return (
    <AnalyticsCard
      title={engagement.networkMembership.title}
      subtitle={engagement.networkMembership.subtitle}
      info={[engagement.networkMembership.info(NETWORK_ROW_LIMIT)]}
      viewData={students}
      belowHeader={
        types.length > 0 ? (
          <Box sx={networkLegendSx}>
            {types.map((type) => (
              <LegendRow key={type} color={colorOf(type)} label={type} />
            ))}
          </Box>
        ) : null
      }
    >
      {rows.length === 0 ? (
        <Typography variant="text-sm-regular" sx={mutedTextSx}>
          {engagement.networkMembership.empty}
        </Typography>
      ) : (
        <Box sx={networkListSx}>
          {rows.map((row) => {
            const pct = percentOf(row.count, max);

            return (
              <Box
                key={row.group_id}
                id={`coaching-analytics-network-row-${row.group_id}`}
                sx={[networkRowSx, selectableRowSx]}
                onClick={() =>
                  students.openSegment({
                    label: row.name,
                    where: [{ field: 'network_ids', op: 'eq', value: row.group_id }],
                  })
                }
              >
                <Typography variant="text-sm-medium" sx={networkLabelSx}>
                  {row.name}
                </Typography>
                <Box sx={networkTrackSx}>
                  <Box
                    sx={networkFillSx(pct > 0 && pct < 100)}
                    style={{
                      width: `${pct}%`,
                      backgroundColor: COLOR_TOKENS[colorOf(row.type)],
                    }}
                  />
                </Box>
                <CountPill value={row.count} />
              </Box>
            );
          })}
        </Box>
      )}
    </AnalyticsCard>
  );
};

export type BarRow = Readonly<{
  id: string;
  label: string;
  /** Fill width as a share of the track. */
  pct: number;
  /** Printed in the chip at the end of the bar. */
  value: number;
  tooltip: string;
  segment: SegmentFilter;
}>;

/** Labelled horizontal bars with a count; with `onSelect`, each bar opens its segment. */
export const BarRows = ({
  rows,
  idPrefix,
  onSelect,
}: Readonly<{
  rows: readonly BarRow[];
  idPrefix: string;
  onSelect?: (segment: SegmentFilter) => void;
}>) => (
  <Box sx={networkListSx}>
    {rows.map((row) => (
      <Tooltip key={row.id} arrow placement="top" title={row.tooltip}>
        <Box
          id={`${idPrefix}-${row.id}`}
          sx={onSelect ? [networkRowSx, selectableRowSx] : networkRowSx}
          onClick={onSelect && (() => onSelect(row.segment))}
        >
          <Typography variant="text-sm-medium" sx={networkLabelSx}>
            {row.label}
          </Typography>
          <Box sx={networkTrackSx}>
            <Box
              sx={networkFillSx(row.pct > 0 && row.pct < 100)}
              style={{ width: `${row.pct}%`, backgroundColor: COLOR_TOKENS[BAR_FILL] }}
            />
          </Box>
          <CountPill value={row.value} />
        </Box>
      </Tooltip>
    ))}
  </Box>
);

/** One bar per profile fact, most common first, as a share of `total`. */
const profileSignalRows = ({ total, signals }: ProfileSignals): BarRow[] =>
  [...signals]
    .sort((a, b) => b.count - a.count)
    .map((signal) => {
      const label = profileSignalLabel(signal.id);
      const pct = percentOf(signal.count, total);

      return {
        id: signal.id,
        label,
        pct,
        value: signal.count,
        tooltip: hover.count(label, signal.count),
        segment: { label, where: [{ field: 'profile_signals', op: 'eq', value: signal.id }] },
      };
    });

const ProfileCompletenessCard = ({ data }: Readonly<{ data: EngagementResponse }>) => {
  const students = useStudentData(
    'coaching-analytics-card-profile-completeness',
    engagement.profile.title,
    PROFILE_COLUMNS,
  );
  const rows = profileSignalRows(data.profile);

  return (
    <AnalyticsCard
      title={engagement.profile.title}
      subtitle={engagement.profile.subtitle}
      info={[engagement.profile.info]}
      id="coaching-analytics-card-profile-completeness"
      viewData={students}
    >
      <BarRows
        rows={rows}
        idPrefix="coaching-analytics-profile-row"
        onSelect={students.openSegment}
      />
    </AnalyticsCard>
  );
};

const AdoptionCard = ({ data }: Readonly<{ data: EngagementResponse }>) => {
  const { completions } = data;
  const students = useStudentData(
    'coaching-analytics-card-success-system-adoption',
    engagement.adoption.title,
    ADOPTION_COLUMNS,
  );
  const bar = (
    id: string,
    label: string,
    counts: EngagementResponse['completions']['blueprint'],
    where: SegmentFilter['where'],
  ): VerticalBarChartBar => ({
    id: `coaching-analytics-adoption-bar-${id}`,
    label,
    value: counts.count,
    tooltip: hover.share(label, counts.count, counts.percentage),
    onClick: () => students.openSegment({ label, where }),
  });
  const bars = [
    bar('blueprint', assessmentCoverage.blueprint, completions.blueprint, [
      { field: 'blueprint_completed', op: 'eq', value: true },
    ]),
    bar('are-you-on-track', assessmentCoverage.areYouOnTrack, completions.are_you_on_track, [
      { field: 'ayot_completed', op: 'eq', value: true },
    ]),
    bar('life-readiness', assessmentCoverage.lifeReadiness, completions.life_readiness, [
      { field: 'life_readiness_status', op: 'eq', value: 'completed' },
    ]),
  ];

  return (
    <AnalyticsCard
      title={engagement.adoption.title}
      subtitle={engagement.adoption.subtitle}
      info={[engagement.adoption.info]}
      id="coaching-analytics-card-success-system-adoption"
      viewData={students}
    >
      <VerticalBarChart
        ariaLabel={engagement.adoption.title}
        outlineColor="Colors/Base/Black"
        bars={bars}
      />
    </AnalyticsCard>
  );
};

/** Bookmark types with at least one bookmark, most-saved first. */
const visibleBookmarkTypes = (
  types: EngagementResponse['bookmarks']['types'],
): EngagementResponse['bookmarks']['types'] =>
  types
    .filter((type) => type.bookmarks > 0 && !HIDDEN_BOOKMARK_TYPES.has(type.id))
    .slice()
    .sort((a, b) => b.bookmarks - a.bookmarks);

const BookmarksCard = ({ data }: Readonly<{ data: EngagementResponse }>) => {
  const types = visibleBookmarkTypes(data.bookmarks.types);
  const max = Math.max(...types.map((type) => type.bookmarks), 0);
  const students = useStudentData(
    'coaching-analytics-card-bookmarks',
    engagement.bookmarks.title,
    BOOKMARK_COLUMNS,
  );
  const rows: BarRow[] = types.map((type) => {
    const label = bookmarkTypeLabel(type.id);

    return {
      id: type.id,
      label,
      pct: percentOf(type.bookmarks, max),
      value: type.bookmarks,
      tooltip: engagement.bookmarks.hover(type.bookmarks, type.students),
      segment: { label, where: [{ field: 'bookmark_types', op: 'eq', value: type.id }] },
    };
  });

  return (
    <AnalyticsCard
      title={engagement.bookmarks.title}
      subtitle={engagement.bookmarks.subtitle}
      info={[engagement.bookmarks.info]}
      id="coaching-analytics-card-bookmarks"
      viewData={students}
    >
      {rows.length === 0 ? (
        <Typography variant="text-sm-regular" sx={mutedTextSx}>
          {engagement.bookmarks.empty}
        </Typography>
      ) : (
        <BarRows
          rows={rows}
          idPrefix="coaching-analytics-bookmark-row"
          onSelect={students.openSegment}
        />
      )}
    </AnalyticsCard>
  );
};

/** Profile Completeness and Success System Adoption side by side, then Bookmarks. */
const EngagementDetail = ({ data }: Readonly<{ data: EngagementResponse }>) => {
  useDetailAgentContext(data, visibleBookmarkTypes(data.bookmarks.types));

  return (
    <>
      <Box sx={bottomGridSx}>
        <ProfileCompletenessCard data={data} />
        <AdoptionCard data={data} />
      </Box>
      <BookmarksCard data={data} />
    </>
  );
};

type EngagementSectionProps = Readonly<{
  data: EngagementResponse;
  /** Rendered Tool Usage card, placed below this section's own cards. */
  toolUsage: ReactNode;
  /** Replaces the header's Lubav button. */
  action?: ReactNode;
  /** Adds the detail-tab cards below the shared ones. */
  detail?: boolean;
}>;

/** The engagement cards keyed by the area names the layouts place them under. */
export const engagementCards = (
  data: EngagementResponse,
  onCardClick?: () => void,
): Record<string, ReactNode> => ({
  recency: <LastActiveCard data={data} onClick={onCardClick} />,
  activity: <ActivityCard data={data} onClick={onCardClick} />,
});

const EngagementTopCards = ({
  data,
  onCardClick,
}: Readonly<{ data: EngagementResponse; onCardClick?: () => void }>) => {
  useTopCardsAgentContext(data);

  return <AnalyticsCardGrid layout={ENGAGEMENT_TOP} cards={engagementCards(data, onCardClick)} />;
};

export const EngagementSection = ({
  data,
  toolUsage,
  action,
  detail = false,
}: EngagementSectionProps) => {
  useAgentContext(data, NETWORK_ROW_LIMIT);

  return (
    <Box sx={sectionSx}>
      <PageHeader
        title={engagement.title}
        subtitle={engagement.subtitle}
        maxWidth={null}
        rightComponents={[
          action ?? (
            <LubavButton
              key="engagement-discuss-lubav"
              id="coaching-analytics-engagement-discuss-lubav"
              label={engagement.discussWithLubav}
              promptButtonId="coaching_analytics_engagement"
            />
          ),
        ]}
      />
      <EngagementTopCards data={data} />
      <Box sx={bottomGridSx}>
        <NetworkMembershipCard data={data} />
        {toolUsage}
      </Box>
      {detail && <EngagementDetail data={data} />}
    </Box>
  );
};

const sectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  width: '100%',
});

const bottomGridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: backrs.spacing['spacing-3xl'],
  alignItems: 'stretch',
  '@container (min-width: 900px)': {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },
});

// Where LineChart's plot area starts and ends, as a share of its viewBox: the
// left clears the value-label gutter it adds under `showValues`. Anything
// aligned to a point needs the same insets.
const AXIS_INSET_LEFT = '7.8%';
const AXIS_INSET_RIGHT = '2.5%';

const ACTIVITY_POINT_RADIUS = 4;
// every activity chart starts at least at this scale so quiet months are not stretched to fill it
const ACTIVITY_SCALE_FLOOR = 20;

const activityChartWrapSx: SxProps<Theme> = {
  position: 'relative',
  width: '100%',
};

// Half-width end cells so each hover region centres on its month's point.
const activityHoverRowSx: SxProps<Theme> = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  paddingLeft: AXIS_INSET_LEFT,
  paddingRight: AXIS_INSET_RIGHT,
  boxSizing: 'border-box',
};

const activityHoverCellSx = (isEdge: boolean): SxProps<Theme> => ({
  flex: isEdge ? 0.5 : 1,
  minWidth: 0,
});

const activityLegendSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexWrap: 'nowrap',
  gap: backrs.spacing['spacing-xl'],
  width: '100%',
});

const weekAxisSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  paddingLeft: AXIS_INSET_LEFT,
  paddingRight: AXIS_INSET_RIGHT,
  boxSizing: 'border-box',
};

const weekLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const networkLegendSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xl'],
  width: '100%',
});

const networkListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
});

const networkRowSx = ({ backrs }: Theme): SystemStyleObject<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
});

/** Half-width card, so names wrap to a second line rather than clipping. */
const networkLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  width: 152,
  flexShrink: 0,
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  wordBreak: 'break-word',
});

const networkTrackSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flex: 1,
  height: 28,
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  backgroundColor: backrs.colors['Brand/50'],
  overflow: 'hidden',
});

const networkFillSx =
  (hasDivider: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    height: '100%',
    boxSizing: 'border-box',
    borderRight: hasDivider ? `1px solid ${backrs.colors['Colors/Base/Black']}` : 'none',
  });
